/**
 * ID8.FUN API 工具函数
 * 用于获取文章数据
 */

// 直接使用外部API，不使用代理
const API_BASE_URL = 'https://api.id8.fun';

/**
 * 发送带CORS处理的请求，包含重试机制
 * @param {string} url - 请求URL
 * @param {Object} options - 请求选项
 * @param {number} retries - 重试次数
 * @returns {Promise<any>} - 响应数据
 */
async function fetchWithCORS(url, options = {}, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // eslint-disable-next-line no-console
      console.log(`Making request to: ${url} (attempt ${attempt + 1})`);

      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'omit',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Origin: window.location.origin,
        },
        ...options,
      });

      // eslint-disable-next-line no-console
      console.log('Response status:', response.status);
      // eslint-disable-next-line no-console
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // eslint-disable-next-line no-console
      console.log('Response data:', data);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`API request failed (attempt ${attempt + 1}):`, error);

      // 如果是最后一次尝试，抛出错误
      if (attempt === retries) {
        // 如果是网络连接错误，提供更友好的错误信息
        if (
          error.message.includes('CORS') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('ERR_CONNECTION_CLOSED') ||
          error.message.includes('ERR_NETWORK')
        ) {
          throw new Error('网络连接不稳定，请稍后重试');
        }

        throw new Error(`API请求失败: ${error.message}`);
      }

      // 等待一段时间后重试
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
}

/**
 * 获取博客文章列表
 * @param {number} page - 页码
 * @param {number} limit - 每页数量
 * @returns {Promise<Object>} 文章列表数据
 */
export const fetchPosts = async (page = 1, limit = 10) => {
  try {
    // 直接使用外部API，不使用代理
    const url = `${API_BASE_URL}/api/public/posts?page=${page}&limit=${limit}`;

    // eslint-disable-next-line no-console
    console.log('Fetching posts from:', url);

    const data = await fetchWithCORS(url);
    // eslint-disable-next-line no-console
    console.log('API Response:', data);

    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching posts:', error);
    throw error;
  }
};

/**
 * 获取文章详情
 * @param {number} id - 文章ID
 * @returns {Promise<Object>} - 文章详情对象
 */
export async function fetchPostDetail(id) {
  try {
    const url = `${API_BASE_URL}/api/public/posts/${id}`;
    // eslint-disable-next-line no-console
    console.log('Fetching post detail from:', url);

    const data = await fetchWithCORS(url);
    // eslint-disable-next-line no-console
    console.log('Post detail received:', data);

    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching post detail:', error);
    throw error;
  }
}

/**
 * 解析标签字符串
 * @param {string} tagsString - 标签字符串，可能是 JSON 或逗号分隔
 * @returns {Array<string>} 标签数组
 */
export const parseTags = tagsString => {
  if (!tagsString) {
    return [];
  }

  try {
    // 尝试解析 JSON 格式
    const parsed = JSON.parse(tagsString);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    // 如果不是 JSON，按逗号分割
    return tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
  }
};

/**
 * 格式化日期
 * @param {string} dateString - ISO 日期字符串
 * @param {string} locale - 语言环境，默认为浏览器语言
 * @returns {string} 格式化后的日期
 */
export const formatDate = (dateString, locale = null) => {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);

    // 如果没有指定locale，使用浏览器的语言设置
    const targetLocale =
      locale || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

    return date.toLocaleDateString(targetLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};
